import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

export function useSearchParamsState<T extends string[], K extends boolean>(
  searchParamNames: [...T],
  defaultValues: string[],
  multiple: true,
): readonly [
  searchParamsState: Record<[...T][number], string[]>,
  setSearchParamsState: (newState: PartialRecord<[...T][number], string | undefined>) => void,
  (values: PartialRecord<[...T][number], string[] | undefined>) => void,
];
export function useSearchParamsState<T extends string[], K extends boolean>(
  searchParamNames: [...T],
  defaultValues?: string[],
  multiple?: false,
): readonly [
  searchParamsState: Record<[...T][number], string | undefined>,
  setSearchParamsState: (newState: PartialRecord<[...T][number], string | undefined>) => void,
  (values: PartialRecord<[...T][number], string[] | undefined>) => void,
];
export function useSearchParamsState<T extends string[], K extends boolean>(
  searchParamNames: [...T],
  defaultValues: string[] = [],
  multiple?: K,
): readonly [
  searchParamsState: Record<[...T][number], K extends true ? string[] : string | undefined>,
  setSearchParamsState: (newState: PartialRecord<[...T][number], string | undefined>) => void,
  (values: PartialRecord<[...T][number], string[] | undefined>) => void,
] {
  const searchParams = useSearchParams();

  const searchParamsState = useMemo(() => {
    return searchParamNames.reduce(
      (acc, searchParamName, index) => {
        let value = multiple
          ? searchParams.getAll(searchParamName)
          : searchParams.get(searchParamName);
        acc[searchParamName as string] =
          value && value !== 'undefined' ? value : defaultValues[index];
        return acc;
      },
      {} as Record<[...T][number], K extends true ? string[] : string | undefined>,
    );
  }, [searchParams, searchParamNames, defaultValues, multiple]);

  const setSearchParamsState = useCallback(
    (values: PartialRecord<[...T][number], string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      Object.keys(values).forEach((key) => {
        if (values[key]) {
          params.set(key, values[key]);
        } else {
          params.delete(key);
        }
      });
      window.history.pushState(null, '', `?${params.toString()}`);
    },
    [searchParams],
  );

  const setMultipleSearchParamsState = useCallback(
    (values: PartialRecord<[...T][number], string[] | undefined>) => {
      const params = new URLSearchParams(searchParams);
      Object.keys(values).forEach((key) => {
        if (values[key]) {
          params.delete(key);
          values[key].forEach((value) => {
            if (!params.getAll(key).includes(value)) {
              params.append(key, value);
            }
          });
        }
      });
      window.history.replaceState(null, '', `?${params.toString()}`);
    },
    [searchParams],
  );

  return [searchParamsState, setSearchParamsState, setMultipleSearchParamsState];
}
